import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, IconButton } from '@material-ui/core';
import { getIn, useFormikContext } from 'formik';
import { appConfig } from 'brand-config';

import useSession from 'core/hooks/use-session';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import pencilIcon from 'core/assets/svg/pencil-orange.svg';
import checkIcon from 'core/assets/svg/check-green.svg';
import closeIcon from 'core/assets/svg/x.svg';
import useStyles from './insurance-score-override.styles';

/**
 * Underwriter insurance-score override (issue #34377).
 *
 * Renders the insurance-score value and, for an underwriter on a brand that allows
 * it, a pencil next to the score. Clicking the pencil turns the score into an
 * inline numeric input; the underwriter edits it and saves (check) or cancels (x).
 * Saving marks the person's `insuranceScoreOverride` boolean true and stores the
 * manual value; clearing the input and saving removes the override (back to the
 * pulled score). The rater uses the manual value for the life of the policy until
 * it is removed. The backend re-derives the effective score and independently gates
 * writes on the underwriter permission, so these gates are display-only.
 *
 * Gated by the `canApproveUWReview` permission AND the brand
 * `featureFlags.insuranceScoreOverride` flag (on for CFM and Oakwood — the mutual
 * brands that rate on credit; off for Forreston, which does not, and for Branch).
 */
const InsuranceScoreOverride = ({ fieldName, pulledScore, isNA }) => {
  const classes = useStyles();
  const { canApproveUWReview } = useSession();
  const { values, setFieldValue } = useFormikContext();
  const [editing, setEditing] = useState(false);
  const priorRef = useRef({ isOverriding: false, overrideValue: null });

  const overridePath = `${fieldName}.insuranceScoreOverride`;
  const valuePath = `${fieldName}.insuranceScoreOverrideValue`;
  const isOverriding = !!getIn(values, overridePath);
  const overrideValue = getIn(values, valuePath);

  const canOverride = Boolean(canApproveUWReview && appConfig?.featureFlags?.insuranceScoreOverride);
  const displayed = isNA ? 'N/A' : resolveDisplayedInsuranceScore(isOverriding, overrideValue, pulledScore);

  const startEdit = () => {
    priorRef.current = { isOverriding, overrideValue };
    // Seed the input with the score currently shown so the underwriter edits the value in place.
    if (!isOverriding && Number.isFinite(Number(pulledScore))) {
      setFieldValue(valuePath, Number(pulledScore));
    }
    setEditing(true);
  };

  const save = () => {
    const current = getIn(values, valuePath);
    if (current === '' || current === null || current === undefined || !Number.isFinite(Number(current))) {
      // Cleared → remove the override and fall back to the pulled score.
      setFieldValue(overridePath, false);
      setFieldValue(valuePath, null);
    } else {
      setFieldValue(overridePath, true);
      setFieldValue(valuePath, Number(current));
    }
    setEditing(false);
  };

  const cancel = () => {
    const { isOverriding: wasOverriding, overrideValue: priorValue } = priorRef.current;
    setFieldValue(overridePath, wasOverriding);
    setFieldValue(valuePath, wasOverriding ? priorValue : null);
    setEditing(false);
  };

  if (editing) {
    return (
      <Grid container alignItems="center" justifyContent="flex-end" wrap="nowrap" className={classes.editRow}>
        <div className={classes.inputWrap}>
          <FormField
            id={valuePath}
            name={valuePath}
            type="numeric"
            mode="light"
            hideErrorMessage
            format={{ decimalScale: 0, allowNegative: false }}
            className={classes.input}
          />
        </div>
        <IconButton className={classes.iconButton} onClick={save} aria-label="Save insurance score">
          <img className={classes.icon} src={checkIcon} alt="Save" />
        </IconButton>
        <IconButton className={classes.iconButton} onClick={cancel} aria-label="Cancel insurance score edit">
          <img className={classes.icon} src={closeIcon} alt="Cancel" />
        </IconButton>
      </Grid>
    );
  }

  return (
    <Grid container alignItems="center" justifyContent="flex-end" wrap="nowrap">
      <Label type="infoValue">{displayed}</Label>
      {canOverride && !isNA && (
        <IconButton
          className={classes.iconButton}
          onClick={startEdit}
          aria-label="Override insurance score"
          title={isOverriding ? 'Manually overridden — edit' : 'Override insurance score'}
        >
          <img className={classes.icon} src={pencilIcon} alt="Edit" />
        </IconButton>
      )}
    </Grid>
  );
};

InsuranceScoreOverride.propTypes = {
  fieldName: PropTypes.string.isRequired,
  pulledScore: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isNA: PropTypes.bool
};

InsuranceScoreOverride.defaultProps = {
  pulledScore: null,
  isNA: false
};

/**
 * The insurance score to display: the manual override value when the override is
 * active with a finite numeric value, otherwise the system-pulled score.
 */
export const resolveDisplayedInsuranceScore = (isOverriding, overrideValue, pulledScore) =>
  // Use the override only when it is a real finite number (0 is valid); null/undefined/'' fall
  // back to the pulled score. Mirrors the rater's isInsuranceScoreOverridden — don't coerce null
  // to 0 via Number().
  isOverriding && typeof overrideValue === 'number' && Number.isFinite(overrideValue) ? overrideValue : pulledScore;

export default InsuranceScoreOverride;
